import { Config } from 'config';

import { forEach, find, last, invokeMap, indexOf, assign } from 'lodash';

import { Base, imagePromise, supportsCrossOriginImages, resolve, forEach$, if$ } from 'gear';

import { Point, Matrix } from '../../geometry';

import { Figures } from '../figures';

// QuantumImage and FiguresCache may only be used in web apps
// The caches may be needed in a per request or per user fashion
// Maybe it is not a bad idea to keep the latest files accessed
// by a user in memory

const stripExtension = function( id ) {
  const idx = id.indexOf('.');
  return idx === -1 ? id : id.slice(0,idx);
};

const promiseThenImage = function( promise, c_id, qs ) {
  return promise .then( () => c_id[qs] );
};

export function downScaleQuantum$( image, qs ) {
  const { width, height } = image;

  if( width < qs && height < qs ) {
    return resolve(image);
  }

  const canvas = document.createElement('canvas');

  const size = Math.max(width,height);

  let sw, sh;
  if( width > height ) {
    sw = 1;
    sh = height/width;
  }
  else {
    sw = width/height;
    sh = 1;
  }
  canvas.width  = qs * sw;
  canvas.height = qs * sh;
  const context = canvas.getContext('2d');
  const scale = qs/size;
  context.scale(scale,scale);
  if (Config.smoothRasterOnZoom !== undefined)
    context.imageSmoothingEnabled = Config.smoothRasterOnZoom;
  context.drawImage(image,0,0);
  return imagePromise(canvas.toDataURL());
}

const quantumSizes = [ 128, 256, 512, 768, 1024 ]; // Check if we need bigger sizes
const previewQuantumSize = 128; // We should get this value from the server, it is hardcoded right now

if( Config.smallerQuantumSizes ) {
  quantumSizes.unshift(64);
}

export const QuantumImageBase = Base.extend({ typeName: 'QuantumImageBase',

  init() {
    this._cache = {};
  },

  cacheOf( id ) {
    const c = this._cache;
    let c_id = c[ id ];
    if( ! c_id ) {
      c_id = c[ id ] = { p : {} };
    }
    return c_id;
  },

  set( id, size, image ) {
    const c_id = this.cacheOf(id);
    const info = c_id.info;
    if( info ) {
      const originalImageSize = Math.max(info.width,info.height);
      if( originalImageSize <= size ) {
        forEach( quantumSizes, qs => {
          if( originalImageSize <= qs ) {
            c_id[qs] = image;
            delete c_id.p[qs];
          }
        });
      }
    }
    c_id[size] = image;
    delete c_id.p[size];
  },

  /* Implemented in the derived classes

  recolorizedId( size,colorMap) {
    return ... id;
  },

  recolorizedImage( id,size,colorMap) {
    return ... image;
  },

  */

  get( id, size, colorMap ) {
    if( colorMap ) {
      return this.recolorizedImage(id,size,colorMap);
    }
    else {
      return this.cacheOf(id)[size];
    }
  },

  getBest( id, size, colorMap ) {
    const _this = this;
    let image = _this.get(id,size,colorMap);
    if( image ) {
      return image;
    }

    const n = indexOf(quantumSizes,size);

    // Look up in bigger scales
    for( let nup = n+1; nup < quantumSizes.length ; ++nup ) {
      const quantumSizes_nup = quantumSizes[nup];
      image = _this.get(id,quantumSizes_nup,colorMap);
      if( image ) {
        return image;
      }
    }

    // Look in smaller scales, result will be pixelated
    for( let ndown = n-1; ndown >= 0; --ndown ) {
      const quantumSizes_ndown = quantumSizes[ndown];
      image = _this.get(id,quantumSizes_ndown,colorMap);
      if( image ) {
        return image;
      }
    }

    return null;
  },

  downScaleQuantumsLocally() {
    return Config.downScaleQuantumsLocally && ( supportsCrossOriginImages || Config.base64Images );
  },

  optimizeQuantumPromises( id, info, size, promise ) {
    const c_id = this.cacheOf(id), localDownScale = this.downScaleQuantumsLocally();

    // Assign the same promise to all quantums that are bigger than the image size
    // Avoid multiple request for bigger sizes

    // Also assign the same promise for smaller quantums in case we are going to
    // use local downscaling

    const originalImageSize = info ? Math.max(info.width,info.height) : 0;
    forEach( quantumSizes, qs => {
      if( ( info && originalImageSize <= qs && originalImageSize < size ) ||
          (  qs < size && localDownScale ) ) {
        c_id.p[qs] = promiseThenImage( promise, c_id, qs );
      }
    });
  },

  // Extension defines loadImage$ that returns an image
  // of the requested size or smaller in case there isn't
  // more pixel information
  cache$( id, size, colorMap ) {
    const _this = this;
    const c_id = _this.cacheOf(id);

    const sr_id = this.recolorizedId(size,colorMap);

    // Image quantum is already cached
    const image = c_id[sr_id];
    if( image ) {
      return resolve(image);
    }

    // We are asking for this quantum
    let promise = c_id.p[sr_id];
    if( promise ) {
      return promise;
    }

    promise = c_id.p[sr_id] = _this.loadImage$(id,size,colorMap) .then( image => {

    _this.set(id,sr_id,image);

    // [TODO] this is not used in FiguresCache, we need to abstract things better here
    //        I leave this one for after the release.
    //        It is not taking into account colorMap here
    //-------------------------------------------------------------------------------
    if( _this.downScaleQuantumsLocally() ) {
      return _this.generateSmallerQuantums$(id,size,image) .then( () => {
      return image;
      });
    }
    return image;
    });
    this.optimizeQuantumPromises(id,c_id.info,size,promise);
    //-------------------------------------------------------------------------------

    return promise;
  },

  generateSmallerQuantums$( id, size, image ) {
    const _this = this;
    const c_id = _this.cacheOf(id);
    const sizes = quantumSizes.slice(0,indexOf(quantumSizes,size));
    sizes.reverse();
    return forEach$( sizes, qs =>
      if$( ! c_id[qs], () => {
        // console.log('downscaling '+Math.max(image.width,image.height)+' > '+qs);
        const promise = c_id.p[qs] = downScaleQuantum$(image, qs) .then( qsImage => {
          _this.set(id,qs,qsImage);
        });
        return promise.then( () => {
          if( qs !== 768 ) { // This is the only size that is not a power of 2
            image = c_id[qs];
            size = qs;
          }
        });
      })
    );
  },

  setInfo( id, info ) {
    const c_id = this.cacheOf(id);
    c_id.info = info;
    delete c_id.info_p;
    return this;
  },
  getInfo( id ) {
    return this.cacheOf(id).info;
  },

  // Extension defines loadInfo$ that returns
  // a info object { width: ..., height: ... }
  info$( id ) {

    const _this = this, c_id = _this.cacheOf(id);

    // We already have th info for this image
    const info = c_id.info;
    if( info ) {
      return resolve( info );
    }

    // We are requesting it to the server, wait for it
    let infoPromise = c_id.info_p;
    if( infoPromise ) {
      return infoPromise;
    }

    // We call read info to get the width and height of the original image.
    // We also ask for a preview to save us one request and be able to show something right
    // away to the user, even if it is pixelated

    infoPromise = c_id.info_p = _this.loadInfo$(id,3) .then( info => {
      _this.setInfo(id,info);
      return info;
    });

    return infoPromise;
  },

  sizeToDraw( vw,vh, wpx,hpx ) {
    let sw, sh;
    if( wpx > hpx ) {
      sw = 1;
      sh = hpx/wpx;
    }
    else {
      sw = wpx/hpx;
      sh = 1;
    }

    const qsize = find( quantumSizes, qs => qs * sw > vw && qs * sh > vh );
    const largestQuantumOrFullImage = Config.downloadFullImageOnLargeZoom ? null : last(quantumSizes);
    const size  = qsize || largestQuantumOrFullImage;

    return size;
  },

  cloneCache( id, aliasId ) {
    this._cache[aliasId] = this._cache[id];
  }
});

export const QuantumImage = new (QuantumImageBase.extend({ typeName: 'QuantumImage',

  recolorizedId( size/*,colorMap*/) {
    return size;
  },

  recolorizedImage( id, size, colorMap ) {
    const image = this.cacheOf(id)[size];
    return image ? colorMap.recolorize(image,id) : null;
  },

  cacheFromFile$( id, file ) {
    // console.log('caching from file');
    const _this = this;
    const c_id = this.cacheOf(id);

    const qs = last(quantumSizes);

    const promise = c_id.info_p = imagePromise(URL.createObjectURL(file)) .then( image => {
    const width = image.width, height = image.height;
    const info = { width: width, height: height };
    _this.setInfo(id,info);

    // If the jump is too big, it can generate artifacts, first perform an intermidiate
    // step, then go to the last quantum.
    return if$( Math.max(width,height) > 2.25*qs, () => {
      return downScaleQuantum$(image,2*qs) .then( image2qs => {
        image = image2qs;
      });
    }) .then( () => {

    return downScaleQuantum$(image,qs) .then( qsImage => {
    _this.set(id,qs,qsImage);

    return _this.generateSmallerQuantums$(id,qs,qsImage) .then( () => {
    return info;

    }); }); }); });

    c_id.p[qs] = promiseThenImage(promise,c_id,qs);
    this.optimizeQuantumPromises(id,c_id.info,qs,promise);

    return promise;
  },

  requestQuantum$( aId, size ) {
    const id = stripExtension(aId);
    const rm = { id, size };
    const manager = Config.GlobalResourceManager;
    if( Config.base64Images ) {
      return manager.renderImage$(rm).then( data => {
      return imagePromise(data);
      });
    }
    else {
      return imagePromise( manager.imageUrl(rm) );
    }
  },

  loadImage$( id, size /*,colorMap */) {

    // The smallest size we can request from the server is previewQuantumSize
    const _this = this;
    if( size !== null && size < previewQuantumSize ) {
      return _this.cache$(id,previewQuantumSize) .then( image => {
      return if$( ! _this.downScaleQuantumsLocally(), () => {
        return _this.generateSmallerQuantums$(id,size,image);
      }) .then( () => {
      return _this.cacheOf(id)[size];
      }); });
    }
    else {
      return _this.requestQuantum$(id,size);
    }
  },

  loadInfo$( id ) {
    const _this = this;
    const rId = stripExtension(id);
    let manager = Config.GlobalResourceManager;
    return manager.imageInfo$({ id: rId,
      preview: Config.rasterPreview || Config.rasterPreviewForced

    }) .then( ({width,height,preview}) => {

    const info = { width, height };
    if( preview ) {
      const qs = previewQuantumSize;
      const promise = _this.cacheOf(id).p[qs] = imagePromise(preview) .then( image => {
        _this.set(id,qs,image);
        return info;
      });
      return promise;
    }
    return info;
    });
  }

}))();

/*
const _testFigures = function() {
  const cdl = '<Cdl> <Shape> <Anchor X="325.4742" Y="639.3244"/> <Transform Matrix22="1,0,0,1"/> <Pin X="325.4742" Y="639.3244" Use="Anchor"/> <Polyregion FillMode="CadX"> <Brush> <Color RGB="FFFFFF" Tag="A"/> </Brush> <Region Points="111.8118 425.6619|539.1367 425.6619|539.1367 852.9869|111.8118 852.9869"/> </Polyregion> </Shape> <Shape> <Anchor X="218.388" Y="749.2761"/> <Transform Matrix22="1,0,0,1"/> <Pin X="219.4448" Y="746.1059" Use="Anchor"/> <Polyregion FillMode="CadX"> <Brush> <Color RGB="2533AA" Tag="B"/> </Brush> <Region Points="111.4317 642.3197|325.3444 642.3197|325.3444 856.2324|111.4317 856.2324"/> </Polyregion> </Shape> <Shape> <Anchor X="436.7761" Y="748.0695"/> <Transform Matrix22="0.7591,0,0,0.7591"/> <Pin X="431.4924" Y="744.8993" Use="Anchor"/> <Polyregion FillMode="CadX"> <Pen Width="10"> <Color RGB="FFFFFF" Tag="E"/> </Pen> <Brush> <Color RGB="2599AA" Tag="D"/> </Brush> <Region Points="328.618 748.0695,328.618 688.3347,377.0413 639.9114|436.7761 639.9114,496.5108 639.9114,544.9342 688.3347|544.9342 748.0695,544.9342 807.8043,496.5108 856.2276|436.7761 856.2276,377.0413 856.2276,328.618 807.8043"/> </Polyregion> </Shape> <Shape> <Anchor X="436.7761" Y="748.0695"/> <Transform Matrix22="0.776,0,0,0.776"/> <Pin X="217.1815" Y="530.888" Use="Anchor"/> <Polyregion FillMode="CadX"> <Brush> <Color RGB="FF4455" Tag="G"/> </Brush> <Region Points="328.618 748.0695,328.618 688.3347,377.0413 639.9114|436.7761 639.9114,496.5108 639.9114,544.9342 688.3347|544.9342 748.0695,544.9342 807.8043,496.5108 856.2276|436.7761 856.2276,377.0413 856.2276,328.618 807.8043"/> </Polyregion> </Shape> <Shape> <Anchor X="218.388" Y="749.2761"/> <Transform Matrix22="1,0,0,1"/> <Pin X="432.3993" Y="531.3375" Use="Anchor"/> <Polyregion FillMode="CadX"> <Brush> <Color RGB="9933AA" Tag="F"/> </Brush> <Region Points="111.4317 642.3197|325.3444 642.3197|325.3444 856.2324|111.4317 856.2324"/> </Polyregion> </Shape> </Cdl>';
  return _.cloneDeep(Document.fromXmlString(cdl).figures;
};
*/

function _zoomFromInfo(info,size) {
  return size / Math.max(info.width,info.height);
}

// [TODO] Do not create one canvas each time?
function createTile_( figures, bounds, zoom, colorMap ) {
  const transform = Matrix.concat( Matrix.scale(zoom),                              // scale quantum to desired size
                                 Matrix.scale(1,-1),                              // invert viewport Y coordinate
                                 Matrix.translate( Point.minus(bounds.lx_hy) ) ); // Move lx_hy to origin
  const canvas = document.createElement('canvas');
  canvas.width  = bounds.width  * zoom;
  canvas.height = bounds.height * zoom;
  const context = canvas.getContext('2d');
  invokeMap(figures,'draw',context,transform,{colorMap:colorMap});
  return canvas;
}

const colorMapCacheList = [];

export const FiguresCache = new (QuantumImageBase.extend({ typeName: 'FiguresCache',

  // [TODO] We need to review this abstraction, caching should
  //        be preview aware
  //        For the moment, we disable color caching in FiguresCache,
  //        because it is not expensive to recompute it

  set( id,sr_id,image) {
    const c_id = this.cacheOf(id);
    c_id[sr_id] = image;
    delete c_id.p[sr_id];

    // Delete old cached results
    colorMapCacheList.push({id:id,sr_id:sr_id});
    if( colorMapCacheList.length > Config.colorMapCacheSize ) {
      const oc = colorMapCacheList.shift();
      delete this.cacheOf(oc.id)[oc.sr_id];
    }
  },

  // Support for tagged colors recolorization with caching

  recolorizedId( size,colorMap) {
    return size + ( colorMap ? '|' + colorMap.textualize() : '' );
  },

  recolorizedImage( id,size,colorMap) {
    const _this = this;
    const sr_id = _this.recolorizedId(size,colorMap);
    const c_id = _this.cacheOf(id);
    const image = c_id[sr_id];
    if( ! image ) {
      const info = c_id.info;
      if( info ) {

        // If we have the info, recolorizing is fast for vector data, just
        // perform the recolorization synchronously
        const figures = info.figures;
        const zoom = _zoomFromInfo(info,size);
        if( Figures.preparedToDraw(figures,zoom) ) {
          const canvas = createTile_(figures,info.bounds,zoom,colorMap);

          // Cache it
          c_id.p[sr_id] = imagePromise(canvas.toDataURL()) .then( image => {
          _this.set(id,sr_id,image);
          });

          return canvas;
        }
      }
    }
    return image;
  },

  downScaleQuantumsLocally() {
    return false;
  },

  optimizeQuantumPromises( /*id,info,size,promise*/) {
    // Do nothing, we have the vector data, so just draw them again.
    // But if the vector data is too complex, maybe is not a bad idea
    // to use images downscaling instead of a full redraw
  },

  loadInfo$( id, retries ) {        
    const _this = this;
    const rId = stripExtension(id);
    return Figures.load$({ Id: rId }) .then( figures => {
    // return g.wait$(400) .then( () =>  _testFigures() ) .then( figures => {
    return Figures.bounds$(figures) .then( bounds => {
    const info = { figures: figures, bounds: bounds.clone(),
                 width: bounds.width, height: bounds.height,
                 prepared: {}
               };

    // Prepare to draw the smallest size, same idea that the QuantumImage preview
    // that we get from the server
    const zoom = _zoomFromInfo(info,previewQuantumSize);
    return Figures.prepareToDraw$(figures,zoom) .then( () => {
    return info;
    });});}).catch(err => {
      if (retries > 0)
        return _this.loadInfo$(id, retries-1);
    });
  },

  loadImage$( id, size, colorMap ) {
    const rId = stripExtension(id);
    return this.info$(rId) .then( info => {
    const figures = info.figures;
    const zoom = _zoomFromInfo(info,size);
    return Figures.prepareToDraw$(figures,zoom) .then( () => {
    const canvas = createTile_(figures,info.bounds,zoom,colorMap);
    // Review, we can just return the canvas here?
    return imagePromise(canvas.toDataURL());
    });});
  }

}))();

// We need to be able to use these two caches in Brushes and ColorMaps
// It should be possible, but webpack is not handling circular dependencies
// correctly, hack it for the moment, use Config as a global variable
assign( Config, { QuantumImage, FiguresCache } );



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/detail/quantum-image.js
 **/